import { ProductLabel } from "../../ui/product-label.tsx";
import { ChannelName } from "../../ui/channel.tsx";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { INBOX_LABELS as I, PLATFORM, POLICY_KIND } from "../../labels.ts";
import { Button, Callout, CopyButton, ErrorText, Field, Kbd, Pill, Reasons } from "../../ui.tsx";
import { Excerpt, PointList, More } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { useInbox, Review, DetailPanelShell } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

export function PostReview() {
  const { postId } = useParams({ strict: false }) as { postId: string };
  const { items, intent, done } = useInbox();
  const post = useQuery(q.postOrder(postId));
  const item = items.find((entry) => entry.kind === "post" && entry.id === postId);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState<"deliver" | "skip" | null>(null);
  const [error, setError] = useState<unknown>();
  const linkRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setLink(""); setError(undefined); }, [postId]);
  useEffect(() => { if (post.data && intent === "approve") linkRef.current?.focus(); }, [intent, post.data, postId]);

  if (post.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!post.data) return <DetailPanelShell><ErrorText error={post.error} /></DetailPanelShell>;
  const data = post.data;
  const failed = data.status === "failed";
  const waiting = (data.status === "approved" || failed) && (data.action_status === "committed" || data.action_status === "failed");
  const act = async (kind: "deliver" | "skip") => {
    setBusy(kind); setError(undefined);
    try {
      if (kind === "deliver") await api.post(`/api/post-orders/${encodeURIComponent(data.id)}/deliver`, { url: link.trim() });
      else await api.post(`/api/post-orders/${encodeURIComponent(data.id)}/skip`);
      done(kind === "deliver" ? I.handedIn : I.skipped);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };
  const channel = PLATFORM[data.channel] ?? data.channel;
  return <Review label={I.detail} title={data.title}
    meta={<><Pill tone={failed ? "red" : waiting ? "teal" : "gray"} status>{I.reason[failed ? "post_link_failed" : "post_ready"]}</Pill>{item && <ProductLabel product={item.product} />}{waiting && !failed && data.due_at && <span>{I.dueBy(data.due_at)}</span>}</>}
    actions={waiting && <><Button tone="primary" type="submit" form="post-link" busy={busy === "deliver"} disabled={busy !== null || !link.trim()}>{I.handIn} <Kbd>A</Kbd></Button><Button tone="soft" disabled={busy !== null} onClick={() => void act("skip")}>{I.skipPost}</Button></>}>
    {!waiting && <Callout>{I.closed}</Callout>}
    <Excerpt className="z-agent-text" lines={2}>{data.why}</Excerpt>
    <dl className="z-props">
      <dt>{I.where}</dt><dd>{I.actionKind[data.kind] ?? data.kind} · <ChannelName channel={data.channel}>{channel}</ChannelName>{!data.target && <> · {I.ownPost}</>}</dd>
      {data.target && <><dt>{I.replyTo}</dt><dd><a href={data.target.url} target="_blank" rel="noreferrer noopener">{data.target.url}</a></dd></>}
      <dt>{I.tier}</dt><dd>{I.authorship[data.authorship] ?? data.authorship}</dd>
      {data.playbook && <><dt>{I.playbook}</dt><dd className="z-mono">{I.playbookVersion(data.playbook.id, data.playbook.version)}</dd></>}
      <dt>{I.measurement}</dt><dd>{I.mode[data.measurement.mode] ?? data.measurement.mode}{data.measurement.window_days ? <> · {I.windowDays(data.measurement.window_days)}</> : null}</dd>
      {data.acceptance.length > 0 && <><dt>{I.acceptance}</dt><dd><PointList items={data.acceptance} /></dd></>}
    </dl>
    {data.draft && <section className="z-flow">
      <h3>{I.draft}</h3>
      <p className="z-small z-muted">{I.draftNote}</p>
      <Excerpt className="z-agent-text" lines={6}>{data.draft}</Excerpt>
      <p><CopyButton text={data.draft} /></p>
    </section>}
    {!data.draft && <section className="z-flow">
      <h3>{I.points}</h3>
      {data.points.length > 0
        ? <><p className="z-small z-muted">{I.pointsNote}</p><ul className="z-points">{data.points.map((point, index) => <li key={index} className="z-agent-text">{point}</li>)}</ul></>
        : <p className="z-small z-muted">{I.templateOnly}</p>}
    </section>}
    {data.policies.length > 0 && <More label={I.rules} count={data.policies.length}>
      <ul className="z-points">{data.policies.map((policy) => <li key={policy.id}><span className="z-muted">{POLICY_KIND[policy.kind as keyof typeof POLICY_KIND] ?? policy.kind}</span> · {policy.rule}{policy.source && <> · <a href={policy.source} target="_blank" rel="noreferrer noopener">{policy.source}</a></>}</li>)}</ul>
    </More>}
    {data.receipt && <dl className="z-props">
      <dt>{I.lastLink}</dt><dd>{data.receipt.url ? <a href={data.receipt.url} target="_blank" rel="noreferrer noopener">{data.receipt.url}</a> : "–"} · {I.verification[data.receipt.verification] ?? data.receipt.verification}</dd>
    </dl>}
    {data.receipt && data.receipt.reasons.length > 0 && <Reasons reasons={data.receipt.reasons} />}
    {waiting && <form id="post-link" className="z-flow" onSubmit={(event) => { event.preventDefault(); if (link.trim()) void act("deliver"); }}>
      <Field label={I.postLink} help={I.postLinkHelp}>
        <input ref={linkRef} className="z-input" type="url" inputMode="url" autoComplete="off" maxLength={2000} value={link} disabled={busy !== null} onChange={(event) => setLink(event.target.value)} />
      </Field>
    </form>}
    <ErrorText error={error} />
  </Review>;
}

/**
 * The reading of an agent's action (plan M4 step 4d-1): what was measured,
 * what the numbers show, the agent's draft, and the values the rules allow.
 */
